/**
 * Manifiesto Page - "¿Qué hace una mejor marca?"
 * Style inspired by forthepeople.agency/better-brands
 * Alternating black/white sections for visual rhythm
 */

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageWrapper } from '../components/EditorialShared';

// Principles
const principles = [
  {
    id: 'vision',
    title: 'VISIÓN',
    subtitle: 'La visión de una mejor marca le permite destacar y romper expectativas.',
    points: [
      'Enfrenta los problemas de su audiencia con valentía',
      'Rompe las convenciones de su categoría',
      'Desafía las suposiciones de su industria',
      'Se atreve a hacer enemigos para ganar verdaderos fans',
    ],
  },
  {
    id: 'estrategia',
    title: 'ESTRATEGIA',
    subtitle: 'La estrategia de una mejor marca le permite inventar nuevos caminos.',
    points: [
      'Tiene ideas transformadoras en su corazón',
      'Contribuye de manera bella al mundo',
      'Piensa en grande, imagina nuevos futuros',
      'Abraza nuevas tecnologías y formas de pensar',
    ],
  },
  {
    id: 'corazon',
    title: 'CORAZÓN',
    subtitle: 'El corazón de una mejor marca le permite conectar auténticamente.',
    points: [
      'Es un movimiento que sus participantes portan con orgullo',
      'Tiene su humanidad auténtica brillando',
      'Es consistente en sus valores y comportamiento',
      'Demuestra empatía real por su audiencia',
    ],
  },
  {
    id: 'alma',
    title: 'ALMA',
    subtitle: 'El alma de una mejor marca recompensa el engagement profundo.',
    points: [
      'Es de calidad excepcional, con atención al detalle',
      'Vive sus ideales al máximo, sin medias tintas',
      'Es rica en significado, con profundidad por descubrir',
      'Trae su idea de marca a la vida en formas únicas',
    ],
  },
  {
    id: 'cuerpo',
    title: 'CUERPO',
    subtitle: 'El cuerpo de una mejor marca le permite resistir el paso del tiempo.',
    points: [
      'Evita cajas restrictivas, enfatizando cohesión',
      'Tiene longevidad gracias a adaptabilidad',
      'Se presenta poderosamente en cada punto de contacto',
      'Evoluciona mientras la cultura cambia',
    ],
  },
];

// Principle section - alternates between black and white
const PrincipleSection = ({ principle, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15%" });
  const isEven = index % 2 === 0;
  
  // Alternate backgrounds
  const isWhiteBg = isEven;
  const bgClass = isWhiteBg ? 'bg-white' : 'bg-black';
  const textColor = isWhiteBg ? 'text-black' : 'text-white';
  const textMuted = isWhiteBg ? 'text-black/60' : 'text-white/60';
  const bulletColor = isWhiteBg ? 'bg-black/30' : 'bg-white/30';

  return (
    <section ref={ref} className={`py-24 md:py-32 ${bgClass}`}>
      <div className="max-w-5xl mx-auto px-6">
        <div className={`grid md:grid-cols-2 gap-12 md:gap-16 items-start`}>
          <motion.div
            className={isEven ? '' : 'md:order-2'}
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className={`${textColor} text-6xl md:text-8xl font-extralight leading-none mb-6`}>
              {principle.title}
            </h2>
            <p className={`${textMuted} text-lg italic`}>
              {principle.subtitle}
            </p>
          </motion.div>

          <motion.div
            className={isEven ? '' : 'md:order-1'}
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <ul className="space-y-5">
              {principle.points.map((point, i) => (
                <motion.li 
                  key={i} 
                  className={`${textMuted} text-lg flex items-start gap-4`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + i * 0.1 }}
                >
                  <span className={`w-2 h-2 ${bulletColor} rounded-full mt-2.5 flex-shrink-0`} />
                  {point}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main component
const ManifiestoPage = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  
  const introRef = useRef(null);
  const isIntroInView = useInView(introRef, { once: true, margin: "-20%" });

  return (
    <PageWrapper showLogoFooter={true}>
      {/* Hero - BLACK background */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-20 bg-black" ref={heroRef}>
        <div className="max-w-4xl text-center">
          <motion.span
            className="text-white/40 text-sm uppercase tracking-[0.2em] mb-8 block"
            initial={{ opacity: 0 }}
            animate={isHeroInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 }}
          >
            Nuestro manifiesto
          </motion.span>
          
          <motion.h1
            className="text-white text-4xl md:text-6xl lg:text-7xl font-extralight leading-tight mb-8"
            initial={{ opacity: 0, y: 60 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.5 }}
          >
            ¿QUÉ HACE UNA
            <br />
            MEJOR MARCA?
          </motion.h1>

          <motion.p
            className="text-white/50 text-lg md:text-xl font-light max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8 }}
          >
            Una mejor marca está hecha para las personas. Cada marca debe abrazar 
            la naturaleza matizada, contradictoria, emotiva y gloriosa de la gente.
          </motion.p>
        </div>
      </section>

      {/* Intro - WHITE background */}
      <section className="py-32 md:py-48 px-6 bg-white" ref={introRef}>
        <div className="max-w-3xl mx-auto">
          <motion.h2
            className="text-black text-3xl md:text-4xl lg:text-5xl font-extralight leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={isIntroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            Una marca no es una persona... pero una mejor marca se construye un poco como una.
          </motion.h2>
          <motion.p
            className="text-black/50 text-lg md:text-xl mt-8"
            initial={{ opacity: 0 }}
            animate={isIntroInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 }}
          >
            Estos son los cinco principios que hacen una mejor marca:
          </motion.p>
          <motion.div
            className="text-black text-3xl mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isIntroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
          >
            ↓
          </motion.div>
        </div>
      </section>

      {/* Principles - Alternating */}
      {principles.map((principle, index) => (
        <PrincipleSection key={principle.id} principle={principle} index={index} />
      ))}

      {/* CTA - BLACK background */}
      <section className="py-32 md:py-48 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-white text-4xl md:text-6xl lg:text-7xl font-extralight mb-8"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            DALE A TU MARCA
            <br />
            LO QUE MERECE
          </motion.h2>

          <motion.p
            className="text-white/50 text-lg md:text-xl mb-12 max-w-xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Si crees que tu marca puede ser mejor, contáctanos.
          </motion.p>

          <motion.div
            className="flex flex-col md:flex-row gap-6 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <a
              href="https://wa.me/527774385617"
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-5 bg-white text-black font-medium hover:bg-white/90 transition-colors"
              data-cursor="escribir"
            >
              Hablemos
            </a>
            <Link
              to="/metodo"
              className="px-10 py-5 border border-white/20 text-white hover:bg-white/10 transition-colors"
              data-cursor="conocer"
            >
              Cómo trabajamos
            </Link>
          </motion.div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default ManifiestoPage;
